import { Select as SelectPrimitive } from "@base-ui/react/select";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import type { ReactNode } from "react";
import { cn, resolveClassName } from "./cn";
import { useDialogSheetMountElement } from "./DialogSheetMount";

export const Select = SelectPrimitive.Root;

export const selectTriggerVariants = cva(
  "inline-flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg border text-left text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      variant: {
        default:
          "border-[var(--border)] bg-[var(--panel)] text-[var(--text)] hover:bg-[var(--hover)]",
        ghost:
          "border-transparent bg-transparent text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)]",
      },
      size: {
        default: "h-9 px-3 text-sm",
        sm: "h-8 px-2.5 text-xs",
        compact: "h-7 px-2 text-xs",
        xs: "h-6 px-2 text-[11px]",
      },
    },
  },
);

export interface SelectTriggerProps
  extends SelectPrimitive.Trigger.Props, VariantProps<typeof selectTriggerVariants> {
  readonly icon?: ReactNode;
}

export function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  icon,
  ...props
}: SelectTriggerProps) {
  const triggerClasses = selectTriggerVariants({ size, variant });
  return (
    <SelectPrimitive.Trigger className={resolveClassName(className, triggerClasses)} {...props}>
      {children}
      <SelectPrimitive.Icon>
        {icon ?? (
          <ChevronDown
            size={size === "xs" || size === "compact" ? 12 : 14}
            className="shrink-0 opacity-60 transition-transform duration-200"
          />
        )}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      className={resolveClassName(className, "min-w-0 flex-1 truncate text-left")}
      {...props}
    />
  );
}

export interface SelectPopupProps extends SelectPrimitive.Popup.Props {
  readonly side?: SelectPrimitive.Positioner.Props["side"];
  readonly sideOffset?: SelectPrimitive.Positioner.Props["sideOffset"];
  readonly align?: SelectPrimitive.Positioner.Props["align"];
  readonly alignOffset?: SelectPrimitive.Positioner.Props["alignOffset"];
  readonly alignItemWithTrigger?: SelectPrimitive.Positioner.Props["alignItemWithTrigger"];
  readonly matchTriggerWidth?: boolean;
  readonly popupClassName?: string;
}

export function SelectPopup({
  className,
  popupClassName,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  alignItemWithTrigger = false,
  matchTriggerWidth = true,
  ...props
}: SelectPopupProps) {
  const defaultPopupClasses = cn(
    "dropdown-glass relative overflow-hidden rounded-lg p-1 text-[var(--text)] shadow-[0_16px_36px_-16px_rgb(0_0_0/75%)] outline-none focus:outline-none",
    matchTriggerWidth && "min-w-[var(--anchor-width)]",
    popupClassName,
  );
  const dialogSheetMount = useDialogSheetMountElement();

  return (
    <SelectPrimitive.Portal container={dialogSheetMount ?? undefined}>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="z-[130] select-none"
      >
        <SelectPrimitive.Popup
          className={resolveClassName(className, defaultPopupClasses)}
          {...props}
        >
          <SelectPrimitive.ScrollUpArrow className="flex h-5 w-full cursor-default items-center justify-center text-[var(--muted)]">
            <ChevronUp size={14} />
          </SelectPrimitive.ScrollUpArrow>
          <SelectPrimitive.List className="max-h-64 overflow-y-auto scrollbar-thin">
            {children}
          </SelectPrimitive.List>
          <SelectPrimitive.ScrollDownArrow className="flex h-5 w-full cursor-default items-center justify-center text-[var(--muted)]">
            <ChevronDown size={14} />
          </SelectPrimitive.ScrollDownArrow>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

export interface SelectItemProps extends SelectPrimitive.Item.Props {
  readonly hideIndicator?: boolean;
}

export function SelectItem({
  className,
  children,
  hideIndicator = false,
  ...props
}: SelectItemProps) {
  const defaultItemClasses =
    "relative flex h-8 min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md px-2 text-xs font-medium text-[var(--text)] outline-none transition-colors select-none data-highlighted:bg-[var(--hover)] data-highlighted:text-[var(--text)] data-selected:font-semibold data-selected:text-[var(--text)] data-disabled:pointer-events-none data-disabled:opacity-50";

  return (
    <SelectPrimitive.Item className={resolveClassName(className, defaultItemClasses)} {...props}>
      <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">
        {children}
      </SelectPrimitive.ItemText>
      {!hideIndicator && (
        <SelectPrimitive.ItemIndicator className="shrink-0 text-[var(--accent-text)]">
          <Check size={14} />
        </SelectPrimitive.ItemIndicator>
      )}
    </SelectPrimitive.Item>
  );
}

export const SelectGroup = SelectPrimitive.Group;

export function SelectGroupLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      className={resolveClassName(
        className,
        "px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted-2)]",
      )}
      {...props}
    />
  );
}

export function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      className={resolveClassName(className, "my-1 h-px bg-[var(--border)]")}
      {...props}
    />
  );
}
